import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Col, Collapse, Progress, Row, Space, Spin, Typography } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import { LoadingContext } from "@/Context/LoadingContext";
import { UserContext } from "@/Context/UserContext";

const { Title, Text, Paragraph } = Typography;

// Clean list-style feedback item with colored hover effect
const FeedbackItem = ({ text, color, hoverBg }) => (
  <div
    className={`feedback-item feedback-item-${color.replace("#", "")}`}
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 6,
      marginBottom: 4,
      cursor: "default",
      transition: "background-color 0.2s ease",
      "--hover-bg": hoverBg,
    }}
  >
    <Text style={{ color: color, fontSize: 13, lineHeight: "1.6", fontWeight: 600 }}>•</Text>
    <Text style={{ fontSize: 13, lineHeight: "1.6", color: "#444" }}>{text}</Text>
  </div>
);

// Section score card component with AI analysis - Collapsible Design
const SectionAnalysisCard = ({ title, aiAnalysis, icon }) => {
  if (!aiAnalysis) return null;

  const { score, whatsGood, whatToImprove, aiSuggestions } = aiAnalysis;

  const getScoreColor = (score) => {
    if (score >= 80) return "#52c41a";
    if (score >= 60) return "#faad14";
    return "#ff4d4f";
  };

  const parseListItems = (text) => {
    if (!text) return [];
    if (Array.isArray(text)) return text;
    return text
      .split(/\n|•|-|\d+\./)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const goodItems = parseListItems(whatsGood);
  const improveItems = parseListItems(whatToImprove);
  const suggestionItems = parseListItems(aiSuggestions);

  // Build collapse items with styled content
  const collapseItems = [];

  if (goodItems.length > 0) {
    collapseItems.push({
      key: "good",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
          <Text style={{ color: "#52c41a", fontWeight: 500, fontSize: 13 }}>What's Good</Text>
          <Text style={{ color: "#52c41a", fontSize: 12, opacity: 0.7 }}>({goodItems.length})</Text>
        </div>
      ),
      children: (
        <div style={{ padding: "4px 0" }}>
          {goodItems.map((item, idx) => (
            <FeedbackItem key={idx} text={item} color="#52c41a" hoverBg="#f6ffed" />
          ))}
        </div>
      ),
    });
  }

  if (improveItems.length > 0) {
    collapseItems.push({
      key: "improve",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 14 }} />
          <Text style={{ color: "#faad14", fontWeight: 500, fontSize: 13 }}>What to Improve</Text>
          <Text style={{ color: "#faad14", fontSize: 12, opacity: 0.7 }}>({improveItems.length})</Text>
        </div>
      ),
      children: (
        <div style={{ padding: "4px 0" }}>
          {improveItems.map((item, idx) => (
            <FeedbackItem key={idx} text={item} color="#faad14" hoverBg="#fffbe6" />
          ))}
        </div>
      ),
    });
  }

  if (suggestionItems.length > 0) {
    collapseItems.push({
      key: "suggestions",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BulbOutlined style={{ color: "#1890ff", fontSize: 14 }} />
          <Text style={{ color: "#1890ff", fontWeight: 500, fontSize: 13 }}>AI Suggestions</Text>
          <Text style={{ color: "#1890ff", fontSize: 12, opacity: 0.7 }}>({suggestionItems.length})</Text>
        </div>
      ),
      children: (
        <div style={{ padding: "4px 0" }}>
          {suggestionItems.map((item, idx) => (
            <FeedbackItem key={idx} text={item} color="#1890ff" hoverBg="#e6f7ff" />
          ))}
        </div>
      ),
    });
  }

  return (
    <Card
      size="small"
      hoverable
      style={{
        borderRadius: 12,
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
      styles={{ body: { padding: 0 } }}
    >
      {/* Header with icon, name and score */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 18px",
          background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        <Space size={12}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(24, 144, 255, 0.2)",
            }}
          >
            {icon}
          </div>
          <Text strong style={{ fontSize: 15, color: "#333" }}>
            {title}
          </Text>
        </Space>
        {score !== undefined && (
          <Progress
            type="circle"
            percent={score}
            size={48}
            strokeWidth={6}
            strokeColor={getScoreColor(score)}
            trailColor="#f0f0f0"
            format={() => (
              <Text strong style={{ fontSize: 14, color: getScoreColor(score) }}>
                {score}
              </Text>
            )}
          />
        )}
      </div>

      {/* Collapsible sections - What's Good open by default */}
      <style>{`
        .section-collapse .ant-collapse-item {
          border-bottom: 1px solid #f0f0f0 !important;
        }
        .section-collapse .ant-collapse-item:last-child {
          border-bottom: none !important;
        }
        .section-collapse .ant-collapse-header {
          padding: 12px 16px !important;
        }
        .section-collapse .ant-collapse-content-box {
          padding: 0 16px 12px 16px !important;
        }
        .section-collapse .ant-collapse-item > .ant-collapse-header {
          align-items: center !important;
        }
        .feedback-item:hover {
          background-color: var(--hover-bg) !important;
        }
      `}</style>
      <Collapse
        className="section-collapse"
        ghost
        expandIconPosition="start"
        defaultActiveKey={["good"]}
        items={collapseItems}
        style={{ backgroundColor: "white" }}
      />
    </Card>
  );
};

const ResumeAnalysis = () => {
  const navigate = useNavigate();
  const { resumeId: routeResumeId } = useParams();
  const fetch = useAuthenticatedFetch();
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { userProfileData } = useContext(UserContext);

  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get resumeId from route param or from user profile
  const resumeId = routeResumeId || userProfileData?.resumeId;

  useEffect(() => {
    if (resumeId) {
      loadResumeData();
    } else {
      setLoading(false);
      setError("No resume found. Please upload a resume first.");
    }
  }, [resumeId]);

  const loadResumeData = async () => {
    try {
      setLoading(true);
      startLoading();

      const response = await fetch.get(`/resume/${resumeId}`);

      if (response?.data) {
        setResumeData(response.data);
      } else {
        setError("Resume not found");
      }
    } catch (err) {
      setError(err.message || "Failed to load resume data");
    } finally {
      setLoading(false);
      stopLoading();
    }
  };

  const handleEnhanceClick = () => {
    navigate(`/app/resume/${resumeId}`);
  };

  const handleBackClick = () => {
    if (resumeId) {
      navigate(`/app/resume/${resumeId}`);
    } else {
      navigate("/app/resume-choice");
    }
  };

  // Calculate overall score from sections
  const calculateOverallScore = (parsedData) => {
    if (!parsedData) return 0;

    const sections = [
      parsedData.personalInfo?.aiAnalysis,
      parsedData.summary?.aiAnalysis,
      parsedData.skills?.aiAnalysis,
      ...(parsedData.experience?.map((exp) => exp.aiAnalysis) || []),
      ...(parsedData.projects?.map((proj) => proj.aiAnalysis) || []),
    ];

    const validScores = sections
      .filter((section) => section && section.score !== undefined)
      .map((section) => section.score);

    if (validScores.length === 0) return 0;

    return Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length);
  };

  const getScoreLevel = (score) => {
    if (score >= 85) return { text: "Excellent", color: "success" };
    if (score >= 70) return { text: "Good", color: "processing" };
    if (score >= 50) return { text: "Needs Work", color: "warning" };
    return { text: "Needs Major Work", color: "error" };
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="resume-analysis-loading">
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Loading AI analysis...</Paragraph>
        </div>
      </AppLayout>
    );
  }

  if (error || !resumeData) {
    return (
      <AppLayout>
        <div className="resume-analysis-error">
          <Alert message="Error" description={error || "Resume not found"} type="error" showIcon />
          <Button onClick={handleBackClick} style={{ marginTop: 16 }}>
            Go Back
          </Button>
        </div>
      </AppLayout>
    );
  }

  const { parsedData } = resumeData;
  const overallScore = calculateOverallScore(parsedData);
  const scoreLevel = getScoreLevel(overallScore);

  return (
    <AppLayout>
      <div className="resume-analysis-page">
        {/* Header Section */}
        <div className="analysis-header">
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackClick} style={{ marginBottom: 16 }}>
            Back
          </Button>

          <Card
            className="score-overview-card"
            style={{
              borderRadius: 12,
              border: "1px solid #e8e8e8",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} md={8} style={{ textAlign: "center" }}>
                <div className="overall-score-display">
                  <Progress
                    type="circle"
                    percent={overallScore}
                    size={120}
                    strokeWidth={8}
                    strokeColor={{
                      "0%": "#1890ff",
                      "100%": "#52c41a",
                    }}
                    format={() => (
                      <div>
                        <div style={{ fontSize: 32, fontWeight: "bold" }}>{overallScore}</div>
                        <div style={{ fontSize: 14, color: "#666" }}>Overall Score</div>
                      </div>
                    )}
                  />
                </div>
              </Col>

              <Col xs={24} md={16}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div>
                    <Title level={2} style={{ margin: 0 }}>
                      <TrophyOutlined style={{ marginRight: 8, color: "#faad14" }} />
                      Your Resume AI Analysis
                    </Title>
                    <Text style={{ marginTop: 8, color: "#666", fontSize: 16, display: "block" }}>
                      We've analyzed each section of your resume using AI. Review the feedback below to understand
                      what's working and what needs improvement.
                    </Text>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    icon={<RocketOutlined />}
                    onClick={handleEnhanceClick}
                    style={{ fontWeight: 600 }}
                  >
                    Enhance My Resume with AI
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Analysis Sections */}
        <div className="analysis-sections">
          <Title level={3} style={{ marginBottom: 24 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Section-by-Section Analysis
          </Title>

          <Row gutter={[16, 16]} align="start">
            {/* Personal Info Analysis */}
            {parsedData?.personalInfo?.aiAnalysis && (
              <Col xs={24} lg={12}>
                <SectionAnalysisCard
                  title="Personal Information"
                  aiAnalysis={parsedData.personalInfo.aiAnalysis}
                  icon={<FileTextOutlined style={{ color: "#1890ff" }} />}
                />
              </Col>
            )}

            {/* Summary Analysis */}
            {parsedData?.summary?.aiAnalysis && (
              <Col xs={24} lg={12}>
                <SectionAnalysisCard
                  title="Professional Summary"
                  aiAnalysis={parsedData.summary.aiAnalysis}
                  icon={<FileTextOutlined style={{ color: "#1890ff" }} />}
                />
              </Col>
            )}

            {/* Skills Analysis */}
            {parsedData?.skills?.aiAnalysis && (
              <Col xs={24} lg={12}>
                <SectionAnalysisCard
                  title="Skills"
                  aiAnalysis={parsedData.skills.aiAnalysis}
                  icon={<ThunderboltOutlined style={{ color: "#722ed1" }} />}
                />
              </Col>
            )}

            {/* Experience Analysis */}
            {parsedData?.experience?.map(
              (exp, index) =>
                exp.aiAnalysis && (
                  <Col xs={24} lg={12} key={`exp-${index}`}>
                    <SectionAnalysisCard
                      title={`${exp.title || "Experience"} at ${exp.company || "Company"}`}
                      aiAnalysis={exp.aiAnalysis}
                      icon={<FileTextOutlined style={{ color: "#52c41a" }} />}
                    />
                  </Col>
                )
            )}

            {/* Education Analysis - Removed as per requirement */}

            {/* Projects Analysis */}
            {parsedData?.projects?.map(
              (proj, index) =>
                proj.aiAnalysis && (
                  <Col xs={24} lg={12} key={`proj-${index}`}>
                    <SectionAnalysisCard
                      title={proj.name || `Project ${index + 1}`}
                      aiAnalysis={proj.aiAnalysis}
                      icon={<BulbOutlined style={{ color: "#1890ff" }} />}
                    />
                  </Col>
                )
            )}

            {/* Certifications Analysis */}
            {parsedData?.certifications?.map(
              (cert, index) =>
                cert.aiAnalysis && (
                  <Col xs={24} lg={12} key={`cert-${index}`}>
                    <SectionAnalysisCard
                      title={cert.name || `Certification ${index + 1}`}
                      aiAnalysis={cert.aiAnalysis}
                      icon={<TrophyOutlined style={{ color: "#52c41a" }} />}
                    />
                  </Col>
                )
            )}
          </Row>
        </div>

        {/* Quick Wins Section */}
        {(() => {
          // Extract all "What to Improve" items from all sections
          const parseListItems = (text) => {
            if (!text) return [];
            if (Array.isArray(text)) return text;
            return text
              .split(/\n|•|-|\d+\./)
              .map((item) => item.trim())
              .filter((item) => item.length > 0);
          };

          const allImprovements = [];

          if (parsedData?.personalInfo?.aiAnalysis?.whatToImprove) {
            parseListItems(parsedData.personalInfo.aiAnalysis.whatToImprove).forEach((item) => {
              allImprovements.push({ section: "Personal Info", text: item });
            });
          }
          if (parsedData?.summary?.aiAnalysis?.whatToImprove) {
            parseListItems(parsedData.summary.aiAnalysis.whatToImprove).forEach((item) => {
              allImprovements.push({ section: "Summary", text: item });
            });
          }
          if (parsedData?.skills?.aiAnalysis?.whatToImprove) {
            parseListItems(parsedData.skills.aiAnalysis.whatToImprove).forEach((item) => {
              allImprovements.push({ section: "Skills", text: item });
            });
          }
          parsedData?.experience?.forEach((exp) => {
            if (exp.aiAnalysis?.whatToImprove) {
              parseListItems(exp.aiAnalysis.whatToImprove).forEach((item) => {
                allImprovements.push({ section: "Experience", text: item });
              });
            }
          });

          // Get top 3 quick wins
          const quickWins = allImprovements.slice(0, 3);

          if (quickWins.length === 0) return null;

          return (
            <div className="quick-wins-section" style={{ marginTop: 32, marginBottom: 32 }}>
              <Title level={3} style={{ marginBottom: 16 }}>
                <BulbOutlined style={{ marginRight: 8 }} />
                Quick Wins
              </Title>
              <Card
                style={{
                  borderRadius: 12,
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
                  Top 3 improvements to boost your score:
                </Text>
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  {quickWins.map((win, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        padding: "14px 16px",
                        background: "#fafafa",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: "#1890ff",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                          {win.section}
                        </Text>
                        <Text style={{ color: "#333", fontSize: 13 }}>{win.text}</Text>
                      </div>
                    </div>
                  ))}
                </Space>
              </Card>
            </div>
          );
        })()}

        {/* Footer CTA */}
        <Card className="analysis-footer-cta" style={{ marginTop: 32 }}>
          <Space direction="vertical" align="center" style={{ width: "100%" }} size="large">
            <Title level={3} style={{ margin: 0 }}>
              Ready to Improve Your Resume?
            </Title>
            <Paragraph style={{ fontSize: 16, textAlign: "center", maxWidth: 600, margin: 0 }}>
              Use our AI-powered chat interface to enhance each section, fix issues, and make your resume stand out
              to recruiters and hiring managers.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={handleEnhanceClick}
              style={{ height: 48, fontSize: 16, fontWeight: 600, paddingLeft: 32, paddingRight: 32 }}
            >
              Enhance My Resume with AI
            </Button>
          </Space>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ResumeAnalysis;
